import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useI18n } from "@/lib/i18n";
import Logo from "@/components/common/Logo";
import { Mail, Loader2, Facebook, Instagram, Twitter, Youtube, Linkedin, Music2 } from "lucide-react";
import { toast } from "sonner";

function usePages() {
  return useQuery({
    queryKey: ["pages"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/pages");
      return data.pages || data || [];
    },
    staleTime: 30 * 60_000,
    retry: 0,
  });
}

/** Iter 784 — owner-managed footer legal pages. Hidden ones are omitted
 *  by the API; new/custom ones appear automatically in owner order. */
function usePolicies() {
  return useQuery({
    queryKey: ["policies"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/policies");
      return Array.isArray(data) ? data : data?.policies || [];
    },
    staleTime: 30 * 60_000,
    retry: 0,
  });
}

const SOCIAL_ICONS = [
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "twitter", label: "Twitter", Icon: Twitter },
  { key: "youtube", label: "YouTube", Icon: Youtube },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "tiktok", label: "TikTok", Icon: Music2 },
];

export default function Footer() {
  const { t } = useI18n(); // Iter 704 — whole-site language
  const { data: pages = [] } = usePages();
  const { data: policies = [] } = usePolicies();
  const { data: settings = {} } = useSiteSettings();
  const [email, setEmail] = useState("");

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/newsletter", {
        email: email.trim(), country: "NZ", source: "web_footer",
      });
      return data;
    },
    onSuccess: () => { toast.success("Subscribed — kia ora!"); setEmail(""); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not subscribe"),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    mut.mutate();
  };

  const footerText = settings.footer_text
    || "Allsale Classified is New Zealand's premium marketplace to buy and sell cars, property, jobs, pets and more — free to post, easy to browse.";

  const socials = SOCIAL_ICONS.filter((s) => settings[s.key]);

  return (
    <footer className="mt-24 border-t border-soft bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2 space-y-4">
          <Logo />
          <p className="text-sm text-slate-500 max-w-sm leading-relaxed" data-testid="footer-tagline">
            {t(footerText)}
          </p>
          <form onSubmit={submit} className="flex items-center gap-2 max-w-sm" data-testid="footer-newsletter">
            <div className="relative flex-1">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="footer-newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.co.nz"
                className="w-full pl-10 pr-3 py-2.5 rounded-full bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
              />
            </div>
            <button type="submit" disabled={mut.isPending} data-testid="footer-newsletter-submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-semibold transition-colors disabled:opacity-70">
              {mut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("Subscribe")}
            </button>
          </form>

          {socials.length > 0 && (
            <div className="pt-2 flex items-center gap-2" data-testid="footer-social">
              {socials.map(({ key, label, Icon }) => (
                <a
                  key={key}
                  href={settings[key]}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  title={label}
                  data-testid={`footer-social-${key}`}
                  className="w-9 h-9 grid place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-[color:var(--brand-blue)] hover:text-white transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <h4 className="font-display font-bold text-slate-900 mb-4">{t("Marketplace")}</h4>
          <ul className="space-y-2 text-sm text-slate-500">
            <li><Link to="/c/cars" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("Cars")}</Link></li>
            <li><Link to="/c/property" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("Property")}</Link></li>
            <li><Link to="/c/jobs" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("Jobs")}</Link></li>
            <li><Link to="/c/mobile" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("Mobile & Electronics")}</Link></li>
            <li><Link to="/deals" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("Deals")}</Link></li>
            <li><Link to="/events" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("Events")}</Link></li>
            <li><Link to="/plan-and-pricing" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("Plans & Pricing")}</Link></li>
            <li><Link to="/list-your-business" data-testid="footer-list-your-business" className="hover:text-[color:var(--brand-blue)] transition-colors">{t("List your business")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-slate-900 mb-4">{t("Company")}</h4>
          {/* WO 806 §4 — footer consolidation: 9 Company links in founder
              order (CMS titles from /classifieds/pages), no duplicates. */}
          <ul className="space-y-2 text-sm text-slate-500" data-testid="footer-company-links">
            {(() => {
              const bySlug = Object.fromEntries(pages.map((p) => [p.slug, p]));
              const known = new Set(["about-us", "help", "safe-buying"]);
              const cms = (slug, fallback) => ({
                to: `/pages/${slug}`,
                label: bySlug[slug]?.title || fallback,
                testId: `footer-page-${slug}`,
              });
              const items = [
                cms("about-us", "About Us"),
                { to: "/blog", label: t("Blog") },
                { to: "/faq", label: t("FAQ") },
                cms("help", "Help & Support"),
                { to: "/contact-us", label: t("Contact us") },
                { to: "/feedback", label: t("Feedback") },
                { to: "/partner-program", label: t("Partner store program"), testId: "footer-partner-program-company" },
                cms("safe-buying", "Safe Buying Guide"),
                ...pages
                  .filter((p) => !known.has(p.slug))
                  .map((p) => ({ to: `/pages/${p.slug}`, label: p.title, testId: `footer-page-${p.slug}` })),
                { href: "/api/sitemap.xml", label: t("Sitemap") },
              ];
              return items.map(({ to, href, label, testId }) => (
                <li key={label}>
                  {href ? (
                    <a href={href} data-testid={testId} className="hover:text-[color:var(--brand-blue)] transition-colors">{label}</a>
                  ) : (
                    <Link to={to} data-testid={testId} className="hover:text-[color:var(--brand-blue)] transition-colors">{label}</Link>
                  )}
                </li>
              ));
            })()}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-slate-900 mb-4">{t("Legal")}</h4>
          {/* WO 806 §4 — Legal group purely from GET /api/policies, in
              server order, linking the canonical /policies/:slug paths. */}
          <ul className="space-y-2 text-sm text-slate-500" data-testid="footer-legal-links">
            {policies.map((p) => (
              <li key={`policy-${p.slug}`}>
                <Link
                  to={`/policies/${p.slug}`}
                  data-testid={`footer-policy-${p.slug}`}
                  className="hover:text-[color:var(--brand-blue)] transition-colors"
                >
                  {p.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-soft py-6 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} {settings.site_name || "Allsale Classified"} · {t("Made in Aotearoa")}
      </div>
    </footer>
  );
}
